import { Link } from "react-router-dom";
import Nav from "@/components/thoratech/Nav";
import Footer from "@/components/thoratech/Footer";
import Seo from "@/seo/Seo";
import { BLOG_POSTS } from "@/content/blog";
import { ArrowUpRight, Clock } from "lucide-react";

export default function BlogIndex() {
  const featured = BLOG_POSTS[0];
  const rest = BLOG_POSTS.slice(1);

  return (
    <>
      <Nav />
      <Seo route="/blog" />
      <main data-testid="blog-index-page" className="bg-white pt-[110px] md:pt-[140px] pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
                Notes from the operations floor
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold leading-[1.02] text-[#01080E]">
              The Thoratech <span className="text-[#2AC0A5]">field journal.</span>
            </h1>
            <p className="mt-6 text-lg text-[#01080E]/75 leading-relaxed">
              Operating notes on running customer operations for industrial and manufacturing
              businesses. Written by the people who do the work, not the people who sell it.
            </p>
          </div>

          {/* Featured post */}
          <Link
            to={`/blog/${featured.slug}`}
            data-testid={`blog-card-${featured.slug}`}
            className="group grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#01080E]/10 border border-[#01080E]/10 hover:bg-[#01080E]/15 transition-colors"
          >
            <div className="lg:col-span-7 bg-[#01080E] overflow-hidden flex items-center justify-center">
              <img
                src={featured.hero}
                alt={featured.heroAlt || featured.title}
                className="w-full h-auto max-h-[460px] object-contain"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-5 bg-white p-8 md:p-12 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#2AC0A5]">
                    Featured · {featured.category}
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight text-[#01080E] group-hover:text-[#2AC0A5] transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-5 text-[15px] text-[#01080E]/70 leading-relaxed">
                  {featured.summary}
                </p>
              </div>
              <div className="mt-8 flex items-center justify-between border-t border-[#01080E]/10 pt-5">
                <span className="font-mono-tech text-xs text-[#01080E]/55 flex items-center gap-2">
                  <Clock size={14} /> {featured.readMinutes} min read · {featured.date}
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#01080E] group-hover:text-[#2AC0A5] transition-colors">
                  Read
                  <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
              </div>
            </div>
          </Link>

          {/* Rest of posts */}
          <div className="mt-px grid grid-cols-1 md:grid-cols-3 gap-px bg-[#01080E]/10 border border-[#01080E]/10 border-t-0">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white flex flex-col hover:bg-[#FAFAF8] transition-colors"
              >
                <div className="overflow-hidden bg-[#01080E] flex items-center justify-center">
                  <img
                    src={p.hero}
                    alt={p.heroAlt || p.title}
                    className="w-full h-auto max-h-[260px] object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="p-7 md:p-8 flex-1 flex flex-col">
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#2AC0A5] mb-4">
                    {p.category}
                  </span>
                  <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight text-[#01080E] group-hover:text-[#2AC0A5] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#01080E]/70 leading-relaxed flex-1">
                    {p.summary}
                  </p>
                  <div className="mt-5 pt-4 border-t border-[#01080E]/10 flex items-center justify-between">
                    <span className="font-mono-tech text-[10px] text-[#01080E]/55 flex items-center gap-1.5">
                      <Clock size={12} /> {p.readMinutes} min read
                    </span>
                    <ArrowUpRight size={16} className="text-[#01080E] group-hover:text-[#2AC0A5] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
